/**
 * Type definitions for condor-api-v1 configuration.
 *
 * Condor is endpoint-lambdas-only (Python). It has NO authorizers, NO SQS queues,
 * and NO queue consumer lambdas.
 *
 * User-editable fields only. architectures and package_type are fixed in Terraform
 * (x86_64, Zip) and are not exposed in the UI.
 *
 * - endpoint_lambdas.defaults: runtime, layer_names[], timeout, memory_size
 *   (NO env_vars, lambda_source, s3_key, or build_number).
 * - endpoint_lambdas.lambdas: lambda_handler, lambda_trigger_path (string | string[]),
 *   timeout, memory_size, snapstart.
 */

/** Default runtime for Condor (Python) endpoint lambda defaults in config.json */
export const CONDOR_DEFAULT_LAMBDA_RUNTIME = 'python3.13';

/**
 * Condor endpoint lambda.
 * architectures and package_type are fixed in Terraform (not user-editable).
 *
 * lambda_trigger_path supports dual-route: a single path (string) or multiple
 * paths (string[]). One path is stored as a string; multiple as an array.
 */
export interface CondorLambda {
  lambda_handler: string;
  /** Single path (string) or multiple paths (string[]) — dual-route support */
  lambda_trigger_path?: string | string[];
  timeout?: number;
  memory_size?: number;
  /** SnapStart toggle (Condor / Python) */
  snapstart?: boolean;
  [key: string]: unknown;
}

/**
 * Condor endpoint lambda defaults.
 * architectures and package_type are fixed in Terraform (not user-editable).
 */
export interface CondorLambdaDefaults {
  /** Default Lambda runtime (e.g. python3.13) */
  runtime?: string;
  /** Lambda layer names applied to all endpoint lambdas */
  layer_names?: string[];
  timeout?: number;
  memory_size?: number;
  [key: string]: unknown;
}

/**
 * Endpoint lambdas group for Condor.
 */
export interface CondorEndpointLambdas {
  defaults?: CondorLambdaDefaults;
  lambdas?: Record<string, CondorLambda>;
  iam_policy_names?: string[];
  iam_policy_names_custom?: string[];
}

/**
 * Encryption settings for Condor.
 * All three booleans default to true when undefined; kms_key_alias is optional
 * (empty = module mints its own per-API key; a value = reuse a shared key by its
 * env-portable alias, using the ${env} token).
 */
export interface CondorEncryption {
  enable_kms_key_rotation?: boolean;
  enable_lambda_env_encryption?: boolean;
  enable_cloudwatch_log_encryption?: boolean;
  /**
   * Env-portable KMS alias template. Use the literal token ${env} for the
   * environment (e.g. "alias/condor-${env}"); Terraform substitutes env_name so
   * one config.json works across every account/environment and every API can
   * share one key. Resolved to a key ARN in terraform-condor-api /
   * terraform-aws-api-service.
   */
  kms_key_alias?: string;
}

/**
 * Complete condor-api-v1 API configuration (endpoint lambdas only).
 */
export interface CondorApiV1Config {
  template_name?: string;
  team?: string;
  component?: string;
  project?: string;
  service_name?: string;
  /** API name (template output); deployed configs may use api_service_name */
  api_name?: string;
  api_service_name?: string;
  api_description?: string;
  api_mapping_key?: string;
  endpoint_lambdas?: CondorEndpointLambdas;
  encryption?: CondorEncryption;
  [key: string]: unknown;
}
